/**
 * INTERNAL-ONLY. Merge the tagged Master SaaS Agreement template (bundled docx)
 * with quote + contact data into a downloadable .docx for redline. Runs entirely
 * in the browser (docxtemplater + pizzip) — no server/worker involvement. Never to
 * be linked from apps/docs or apps/marketing.
 */
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import ncaTemplateUrl from '../assets/nca-template.docx?url';
import ndaTemplateUrl from '../assets/nda-template.docx?url';
import templateUrl from '../assets/saas-agreement-template.docx?url';

/** Every merge field in the tagged template (see scripts/build-saas-template.mjs). */
export interface AgreementData {
  effectiveDate: string;
  providerLegalName: string;
  providerEntity: string; // template prints "a {providerEntity}" — omit the leading "a"
  providerAddress: string;
  customerLegalName: string;
  customerEntity: string;
  customerAddress: string;
  customerEmail: string;
  billingContact: string;
  orderDate: string;
  startDate: string;
  termLength: string;
  // Exhibit A pricing row
  planService: string;
  usageMetric: string;
  unitRate: string;
  includedVolume: string;
  minimumFee: string;
  priceNotes: string;
}

/** Fetch a bundled .docx template, merge `data`, return a .docx Blob. Missing keys
 *  render blank (nullGetter). Browser-only. */
async function renderTemplate(url: string, data: AgreementData): Promise<Blob> {
  const buf = await fetch(url).then((r) => r.arrayBuffer());
  const doc = new Docxtemplater(new PizZip(buf), {
    paragraphLoop: true,
    linebreaks: true,
    // Leave any unexpected/unfilled tag blank rather than throwing.
    nullGetter: () => '',
  });
  doc.render(data);
  return doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

/** The master SaaS agreement, merged with the quote + contact fields. */
export function generateAgreementDocx(data: AgreementData): Promise<Blob> {
  return renderTemplate(templateUrl, data);
}

/**
 * The Mutual Confidentiality & Data Protection Agreement (NDA) and the
 * Non-Circumvention & Non-Solicitation Agreement (NCA). Both merge only the agency
 * party fields + effective date (provider details + NJ venue are fixed in the
 * masters); they accept `AgreementData` and ignore the pricing/provider keys.
 */
export function generateNdaDocx(data: AgreementData): Promise<Blob> {
  return renderTemplate(ndaTemplateUrl, data);
}
export function generateNcaDocx(data: AgreementData): Promise<Blob> {
  return renderTemplate(ncaTemplateUrl, data);
}

/** Trigger a browser download of a Blob under `filename`. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
